"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { activityApi } from "@/lib/api/services/activity.api";
import { Card, CardContent } from "@/components/ui/card";
import { ActivityIcon, Clock } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";

interface ActivityItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export default function ActivityPage() {
  const [filter, setFilter] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const res = await activityApi.listMine();
      return ((res as unknown as { data?: ActivityItem[] }).data ?? []) as ActivityItem[];
    },
  });

  const activities = data ?? [];
  const filtered = filter === "all" ? activities : activities.filter((a) => a.action === filter);

  const filters = [
    { value: "all", label: "All Actions" },
    { value: "create", label: "Created" },
    { value: "update", label: "Updated" },
    { value: "delete", label: "Deleted" },
    { value: "login", label: "Logins" },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Activity</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground"
          aria-label="Filter by action"
        >
          {filters.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted/10 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No activity found"
          description="Your activity will appear here."
          icon={ActivityIcon}
          variant="default"
        />
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border/60">
            {filtered.map((activity) => (
              <div key={activity.id} className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/10">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <ActivityIcon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground capitalize">{activity.action}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed truncate">{activity.entityType} — {activity.entityId}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{new Date(activity.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
